import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config/app.config';
import { getProfile, login as loginApi, logout as logoutApi } from '../services/authService';
import { Schemas } from '../types/api-types';

type AuthUser = Schemas['AuthResponse'];
type AuthSession = {
    user: AuthUser;
    loggedInAt: string;
};

interface AuthContextType {
    user: null | AuthUser;
    loggedInAt: string | null;
    sessionUser: AuthSession | null;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    fetchProfile: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_SESSION_STORAGE_KEY = 'webpos.auth.session';
const SESSION_TIMEOUT_MS = config.sessionTimeoutMinutes * 60 * 1000;

const isSessionExpired = (loggedInAt: string): boolean => {
    const loggedInAtTimestamp = new Date(loggedInAt).getTime();
    if (Number.isNaN(loggedInAtTimestamp)) {
        return true;
    }

    return Date.now() - loggedInAtTimestamp >= SESSION_TIMEOUT_MS;
};

const readStoredSession = (): AuthSession | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as Partial<AuthSession>;
        if (!parsed?.user || !parsed?.loggedInAt) {
            return null;
        }

        return {
            user: parsed.user,
            loggedInAt: parsed.loggedInAt,
        };
    } catch {
        return null;
    }
};

const writeStoredSession = (session: AuthSession | null) => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!session) {
        window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
        return;
    }

    window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
};

const readActiveStoredSession = (): AuthSession | null => {
    const session = readStoredSession();
    if (!session) {
        return null;
    }

    if (isSessionExpired(session.loggedInAt)) {
        writeStoredSession(null);
        return null;
    }

    return session;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const initialSessionRef = useRef<AuthSession | null>(readActiveStoredSession());
    const [sessionUser, setSessionUser] = useState<AuthSession | null>(() => initialSessionRef.current);
    const [user, setUser] = useState<null | AuthUser>(() => initialSessionRef.current?.user ?? null);
    const [loggedInAt, setLoggedInAt] = useState<string | null>(() => initialSessionRef.current?.loggedInAt ?? null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const hasInitialized = useRef(false);
    const sessionTimeoutRef = useRef<number | null>(null);
    const navigate = useNavigate();

    const clearSessionTimeout = useCallback(() => {
        if (sessionTimeoutRef.current !== null && typeof window !== 'undefined') {
            window.clearTimeout(sessionTimeoutRef.current);
            sessionTimeoutRef.current = null;
        }
    }, []);

    const updateSession = useCallback((nextUser: AuthUser | null, nextLoggedInAt?: string | null) => {
        clearSessionTimeout();

        if (!nextUser) {
            setUser(null);
            setLoggedInAt(null);
            setSessionUser(null);
            setIsAuthenticated(false);
            writeStoredSession(null);
            return;
        }

        const resolvedLoggedInAt = nextLoggedInAt ?? loggedInAt ?? new Date().toISOString();
        const nextSession = {
            user: nextUser,
            loggedInAt: resolvedLoggedInAt,
        };

        setUser(nextUser);
        setLoggedInAt(resolvedLoggedInAt);
        setSessionUser(nextSession);
        setIsAuthenticated(true);
        writeStoredSession(nextSession);
    }, [clearSessionTimeout, loggedInAt]);

    const completeLogout = useCallback(async (callApi = true) => {
        try {
            if (callApi) {
                await logoutApi();
            }
        } catch {
            // Ignore logout API failures and clear the local session anyway.
        } finally {
            updateSession(null);
            setLoading(false);
            navigate('/login', { replace: true });
        }
    }, [navigate, updateSession]);

    const fetchProfile = useCallback(async () => {
        const activeSession = readActiveStoredSession();
        if (activeSession && isSessionExpired(activeSession.loggedInAt)) {
            updateSession(null);
            return;
        }

        try {
            setLoading(true);
            const nextUser = await getProfile();
            updateSession(nextUser);
        } catch {
            updateSession(null);
        } finally {
            setLoading(false);
        }
    }, [updateSession]);

    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            void fetchProfile();
        }
    }, [fetchProfile]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleAuthExpired = (event: Event) => {
            const shouldRedirect = (event as CustomEvent<{ redirect?: boolean }>).detail?.redirect ?? true;
            updateSession(null);
            setLoading(false);

            if (shouldRedirect) {
                navigate('/login', { replace: true });
            }
        };

        window.addEventListener('webpos:auth-expired', handleAuthExpired as EventListener);
        return () => {
            window.removeEventListener('webpos:auth-expired', handleAuthExpired as EventListener);
        };
    }, [navigate, updateSession]);

    useEffect(() => {
        clearSessionTimeout();

        if (!sessionUser || typeof window === 'undefined') {
            return;
        }

        const expiresAt = new Date(sessionUser.loggedInAt).getTime() + SESSION_TIMEOUT_MS;
        const remainingTime = expiresAt - Date.now();

        if (remainingTime <= 0) {
            void completeLogout();
            return;
        }

        sessionTimeoutRef.current = window.setTimeout(() => {
            void completeLogout();
        }, remainingTime);

        return clearSessionTimeout;
    }, [clearSessionTimeout, completeLogout, sessionUser]);

    const login = async (username: string, password: string) => {
        try {
            setLoading(true);
            const loginResponse = await loginApi(username, password);
            updateSession(loginResponse, new Date().toISOString());
            return true;
        } catch (error) {
            updateSession(null);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = useCallback(async () => {
        setLoading(true);
        await completeLogout();
    }, [completeLogout]);

    return (
        <AuthContext.Provider value={{ user, loggedInAt, sessionUser, isAuthenticated, loading, login, logout, fetchProfile }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
